import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { UserContext } from "../context/UserContext";

/**
 * ConditionalContent component to control access to its children based on user roles and authentication status.
 *
 * @param {Object} props - Component properties.
 * @param {Array} [props.hasOneOfRequiredRoles] - Array of roles; user must have at least one.
 * @param {Array} [props.hasAllOfRequiredRoles] - Array of roles; user must have all.
 * @param {JSX.Element} [props.fallback] - Fallback component to display if conditions are not met.
 *
 * @returns {JSX.Element} The ConditionalContent component.
 */
const ConditionalContent = ({ hasOneOfRequiredRoles, hasAllOfRequiredRoles, fallback, children }) => {
    const { user } = useContext(UserContext);
    const [isAllowed, setIsAllowed] = useState(true);

    useEffect(() => {
        const userRoles = user.roles || [];

        const hasOneOfRoles = hasOneOfRequiredRoles
            ? hasOneOfRequiredRoles.some(role => userRoles.includes(role))
            : true;

        const hasAllOfRoles = hasAllOfRequiredRoles
            ? hasAllOfRequiredRoles.every(role => userRoles.includes(role))
            : true;

        setIsAllowed(hasOneOfRoles && hasAllOfRoles);
    }, [user.roles, hasOneOfRequiredRoles, hasAllOfRequiredRoles]);

    if (!isAllowed) {
        return fallback || null;
    }

    return (
        <>
            {children}
        </>
    );
};

ConditionalContent.propTypes = {
    hasOneOfRequiredRoles: PropTypes.arrayOf(PropTypes.string),
    hasAllOfRequiredRoles: PropTypes.arrayOf(PropTypes.string),
    fallback: PropTypes.element,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node
    ]).isRequired
};

ConditionalContent.defaultProps = {
    hasOneOfRequiredRoles: null,
    hasAllOfRequiredRoles: null,
    fallback: null,
};

export default ConditionalContent;
